// ─── Aviso.jsx ────────────────────────────────────────────────────────────────
// Componente inyectable de avisos de alertas.
// Uso: <Aviso userRole="import" />  |  <Aviso userRole="maestro" />
// Se puede importar desde cualquier otro módulo:
//   import Aviso from "../AlertasDashboard/Aviso";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, ModalHeader, ModalBody, Nav, NavItem, NavLink, Spinner } from "reactstrap";
import {
  ALERT_META, GROUP_CONFIG, GROUP_ALERT_KEYS,
  filterDespachosByType, useAlertsFetch,
} from "./componentes/requests";
import { AlertDataTable } from "./componentes/modal";

// ─── Styles (inyectados una vez, no dependen de CSS externo) ──────────────────
{
  let _s = document.getElementById("aviso-styles");
  if (!_s) {
    _s = document.createElement("style");
    _s.id = "aviso-styles";
    document.head.appendChild(_s);
  }
  _s.textContent = `
    /* ── Modal container ───────────────────────────────────────── */
    .aviso-modal .modal-dialog {
      background: transparent;
    }
    .aviso-modal .modal-content {
      border: none; border-radius: 14px;
      box-shadow: 0 15px 35px rgba(50,50,93,.1), 0 5px 15px rgba(0,0,0,.07);
      max-height: 90vh; display: flex; flex-direction: column; overflow: hidden;
    }
    .aviso-modal .modal-header {
      background: linear-gradient(135deg, #f6851f 0%, #e05a00 100%);
      color: white; border-bottom: none;
      border-radius: 14px 14px 0 0; padding: 16px 24px; flex-shrink: 0;
    }
    .aviso-modal .modal-header .modal-title,
    .aviso-modal .modal-header .modal-title em { color: white !important; }
    .aviso-modal .modal-header .btn-close { filter: invert(1); }
    .aviso-modal .modal-body { overflow-y: auto; padding: 20px 22px; flex: 1; }

    /* ── Group-level nav tabs (maestro: IMPO / EXPO / MRO) ─────── */
    .aviso-group-tabs > .nav-tabs {
      border-bottom: 2px solid #e5e9f2;
      flex-wrap: wrap; gap: 0; margin-bottom: 18px;
    }
    .aviso-group-tabs > .nav-tabs .nav-link {
      border: none; border-bottom: 3px solid transparent;
      font-size: .875rem; font-weight: 600; color: #526484;
      padding: 9px 20px; margin-bottom: -2px; cursor: pointer;
      display: inline-flex; align-items: center; gap: 6px;
      background: transparent; border-radius: 0; transition: background .15s;
    }
    .aviso-group-tabs > .nav-tabs .nav-link:hover {
      background: #f8f9fc;
    }

    /* ── Rule-level pills ───────────────────────────────────────── */
    .aviso-rule-pills {
      flex-wrap: wrap; gap: 8px;
      padding-bottom: 14px; border-bottom: 1px solid #e5e9f2; margin-bottom: 0;
    }
    .aviso-rule-pills .nav-link {
      border-radius: 20px; font-size: .78rem; font-weight: 600;
      padding: 6px 15px; color: #526484; background: #f5f6fa;
      border: 1px solid #e5e9f2; transition: all .2s; cursor: pointer;
      display: inline-flex; align-items: center; gap: 5px; white-space: nowrap;
    }
    .aviso-rule-pills .nav-link:hover {
      background: rgba(102,126,234,.08); color: #5e72e4; border-color: #d0d6f5;
    }
    .aviso-rule-pills .nav-link.active {
      background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
      color: white !important; border-color: transparent;
      box-shadow: 0 4px 10px rgba(139,92,246,.35);
    }
    .aviso-rule-pills .nav-link.active em {
      color: white !important;
    }

    /* ── Trigger button ─────────────────────────────────────────── */
    .aviso-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 7px 15px; border-radius: 8px; border: none;
      font-size: .83rem; font-weight: 600; cursor: pointer;
      transition: transform .15s, box-shadow .15s; line-height: 1.4;
    }
    .aviso-btn:hover { transform: translateY(-1px); }
    .av-cnt {
      border-radius: 20px; font-size: .65rem; font-weight: 700;
      padding: 2px 7px; line-height: 1.3; color: white;
    }
  `;
}

// ─── Helpers de módulo ────────────────────────────────────────────────────────

const SEV_COLOR = { danger: "#e85347", warning: "#f4bd0e", info: "#09c2de" };

// Grupos visibles en Aviso (DESPACHOS no es tab propio — aparece bajo IMPO/EXPO)
const AVISO_GROUPS = ["IMPO", "EXPO", "MRO"];

/** Rol → grupo único de display, o null para maestro/general */
const getSingleGroup = (role) => {
  if (["import", "import admin"].includes(role)) return "IMPO";
  if (["export", "export admin"].includes(role)) return "EXPO";
  if (["mro", "mro admin"].includes(role))       return "MRO";
  return null;
};

/** Claves a mostrar bajo un grupo de display: propias + DESPACHOS para IMPO/EXPO */
const keysForGroup = (displayGroup) => {
  if (displayGroup === "MRO") return GROUP_ALERT_KEYS.MRO;
  return [...GROUP_ALERT_KEYS.DESPACHOS, ...GROUP_ALERT_KEYS[displayGroup]];
};

/** Filas efectivas para una clave en el contexto de un grupo de display */
const effRows = (alertKey, displayGroup, alerts) => {
  const raw = Array.isArray(alerts?.[alertKey]) ? alerts[alertKey] : [];
  if (ALERT_META[alertKey]?.group === "DESPACHOS" && displayGroup !== "MRO")
    return filterDespachosByType(raw, displayGroup);
  return raw;
};

/** Total de alertas para un grupo de display */
const totalForGroup = (displayGroup, alerts) =>
  keysForGroup(displayGroup).reduce((s, k) => s + effRows(k, displayGroup, alerts).length, 0);

// ─── RulePills ────────────────────────────────────────────────────────────────

const RulePills = ({ displayGroup, alerts, activeKey, onSelect }) => {
  const available = keysForGroup(displayGroup)
    .filter(k => effRows(k, displayGroup, alerts).length > 0);

  if (!available.length)
    return <p className="text-soft text-center py-4 mb-0">Sin alertas activas en este grupo.</p>;

  return (
    <Nav className="aviso-rule-pills">
      {available.map(k => {
        const meta  = ALERT_META[k];
        const cnt   = effRows(k, displayGroup, alerts).length;
        const sc    = SEV_COLOR[meta?.severity] || "#8094ae";
        const isAct = activeKey === k;
        return (
          <NavItem key={k}>
            <NavLink active={isAct} onClick={() => onSelect(k)} style={{ cursor: "pointer", color: isAct ? "white" : undefined }}>
              <em
                className={`icon ni ni-${meta?.icon || "alert"}`}
                style={{ color: isAct ? "white" : sc, fontSize: ".88rem", marginRight: 3 }}
              />
              {meta?.label}
              <span className="av-cnt" style={{
                marginLeft: 5,
                background: isAct ? "rgba(255,255,255,.9)" : sc,
                color: isAct ? sc : "white",
              }}>
                {cnt}
              </span>
            </NavLink>
          </NavItem>
        );
      })}
    </Nav>
  );
};

// ─── GroupPanel (pills + tabla para un grupo de display) ─────────────────────

const GroupPanel = ({ displayGroup, alerts, loading }) => {
  const [activeKey, setActiveKey] = useState(null);

  useEffect(() => {
    const first = keysForGroup(displayGroup)
      .find(k => effRows(k, displayGroup, alerts).length > 0) || null;
    setActiveKey(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayGroup, alerts]);

  if (loading)
    return (
      <div className="d-flex align-items-center justify-content-center py-4">
        <Spinner size="sm" color="secondary" />
        <span className="ms-2 text-soft" style={{ fontSize: ".83rem" }}>Cargando alertas…</span>
      </div>
    );

  return (
    <>
      <RulePills
        displayGroup={displayGroup}
        alerts={alerts}
        activeKey={activeKey}
        onSelect={setActiveKey}
      />
      {activeKey && (
        <div style={{ paddingTop: 14 }}>
          {/* filterType=null porque effRows ya pre-filtra filas de DESPACHOS */}
          <AlertDataTable
            alertKey={activeKey}
            rows={effRows(activeKey, displayGroup, alerts)}
            filterType={null}
          />
        </div>
      )}
    </>
  );
};

// ─── AvisoModalBody ───────────────────────────────────────────────────────────

const AvisoModalBody = ({ role, alerts, loadingGroups }) => {
  const singleGroup = getSingleGroup(role);
  const [activeGroup, setActiveGroup] = useState(singleGroup || "IMPO");

  // Rol específico: vista plana (solo un grupo)
  if (singleGroup) {
    const isLoading = Object.values(loadingGroups).some(Boolean);
    return <GroupPanel displayGroup={singleGroup} alerts={alerts} loading={isLoading} />;
  }

  // Maestro/general: tabs de grupo + detalle
  return (
    <div className="aviso-group-tabs">
      <Nav tabs>
        {AVISO_GROUPS.map(g => {
          const cfg = GROUP_CONFIG[g];
          const isGroupLoading = g === "MRO"
            ? !!loadingGroups["MRO"]
            : (!!loadingGroups["DESPACHOS"] || !!loadingGroups[g]);
          const total  = totalForGroup(g, alerts);
          const isAct  = activeGroup === g;
          return (
            <NavItem key={g}>
              <NavLink
                active={isAct}
                onClick={() => setActiveGroup(g)}
                style={{
                  cursor: "pointer",
                  borderBottom: isAct ? `3px solid ${cfg.color}` : "3px solid transparent",
                  color: isAct ? cfg.color : "#526484",
                }}
              >
                <em className={`icon ni ni-${cfg.icon}`} style={{ fontSize: ".9rem" }} />
                {cfg.label}
                {isGroupLoading
                  ? <Spinner size="sm" style={{ width: 12, height: 12, marginLeft: 6 }} />
                  : (
                    <span className="av-cnt" style={{
                      marginLeft: 6,
                      background: total > 0 ? cfg.color : "#c4cde0",
                    }}>
                      {total}
                    </span>
                  )
                }
              </NavLink>
            </NavItem>
          );
        })}
      </Nav>

      <div style={{ paddingTop: 16 }}>
        <GroupPanel
          displayGroup={activeGroup}
          alerts={alerts}
          loading={
            activeGroup === "MRO"
              ? !!loadingGroups["MRO"]
              : (!!loadingGroups["DESPACHOS"] || !!loadingGroups[activeGroup])
          }
        />
      </div>
    </div>
  );
};

// ─── Aviso (componente raíz inyectable) ───────────────────────────────────────
// Props:
//   userRole  – rol del usuario (string)
//   autoOpen  – si true, abre el modal automáticamente una vez que carga
//   onOpen    – callback() invocado cuando el modal se abre automáticamente (oculta overlay)
//   onDismiss – callback() invocado cuando se cierra el modal en modo autoOpen

const Aviso = ({ userRole, autoOpen = false, onOpen, onDismiss }) => {
  const [isOpen, setIsOpen] = useState(false);
  const autoOpened = useRef(false);

  const toggle = () => {
    setIsOpen(o => {
      const next = !o;
      if (!next && autoOpen && onDismiss) onDismiss();
      return next;
    });
  };

  const role = (userRole || "").toLowerCase().trim();
  const { alerts, loadingGroups } = useAlertsFetch(role);

  const isLoading = Object.values(loadingGroups).some(Boolean);

  // Abrir automáticamente una vez terminada la carga (solo si autoOpen)
  // Object.keys(loadingGroups).length > 0 garantiza que el fetch ya inició
  useEffect(() => {
    if (autoOpen && !isLoading && !autoOpened.current && Object.keys(loadingGroups).length > 0) {
      autoOpened.current = true;
      setIsOpen(true);
      if (onOpen) onOpen(); // avisa al padre para ocultar el overlay
    }
  }, [autoOpen, isLoading, loadingGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const singleGroup   = getSingleGroup(role);
  const displayGroups = singleGroup ? [singleGroup] : AVISO_GROUPS;

  const totalAlerts = useMemo(
    () => displayGroups.reduce((s, g) => s + totalForGroup(g, alerts), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alerts],
  );

  // Peor severidad con alertas activas → color del botón
  const btnSeverity = useMemo(() => {
    for (const sev of ["danger", "warning", "info"]) {
      if (Object.keys(alerts).some(
        k => ALERT_META[k]?.severity === sev && alerts[k]?.length > 0
      )) return sev;
    }
    return null;
  }, [alerts]);

  const btnStyle = (() => {
    if (isLoading || !btnSeverity)
      return { background: "#f5f6fa", color: "#526484", border: "1px solid #e5e9f2" };
    if (btnSeverity === "danger")
      return { background: "linear-gradient(135deg,#ff6b6b 0%,#e84848 100%)", color: "white", boxShadow: "0 4px 12px rgba(232,72,72,.3)" };
    if (btnSeverity === "warning")
      return { background: "linear-gradient(135deg,#ffc107 0%,#e6a800 100%)", color: "white", boxShadow: "0 4px 12px rgba(230,168,0,.3)" };
    return { background: "linear-gradient(135deg,#17a2b8 0%,#117a8b 100%)", color: "white", boxShadow: "0 4px 12px rgba(17,122,139,.3)" };
  })();

  return (
    <>
      {/* Botón trigger (solo visible en uso manual, no en modo autoOpen-only) */}
      {!autoOpen && (
        <button className="aviso-btn" onClick={toggle} style={btnStyle}>
          {isLoading
            ? <Spinner size="sm" style={{ width: 14, height: 14 }} />
            : <em className={`icon ni ni-${totalAlerts > 0 ? "alert-circle" : "check-circle"}`} />
          }
          Alertas
          {!isLoading && (
            <span className="av-cnt" style={{ background: totalAlerts > 0 ? "rgba(0,0,0,.18)" : "#c4cde0" }}>
              {totalAlerts}
            </span>
          )}
        </button>
      )}

      <Modal isOpen={isOpen} toggle={toggle} size="xl" className="aviso-modal" scrollable>
        <ModalHeader toggle={toggle} style={{ color: "white" }}>
          <em className="icon ni ni-bell" style={{ marginRight: 8 }} />
          Alertas activas{singleGroup ? ` — ${singleGroup}` : ""}
          {!isLoading && (
            <span style={{
              marginLeft: 10, background: "#f4bd0e",
              color: "#3d2400", borderRadius: 20, fontSize: ".75rem",
              padding: "3px 10px", fontWeight: 700,
            }}>
              {totalAlerts} alerta{totalAlerts !== 1 ? "s" : ""}
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          <AvisoModalBody role={role} alerts={alerts} loadingGroups={loadingGroups} />
        </ModalBody>
      </Modal>
    </>
  );
};

export default Aviso;
